import React from 'react';
import { DatePicker, TimePicker, Input, Button } from 'antd';
import {
    ArrowLeftOutlined,
    WifiOutlined,
    InfoCircleOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    NumberOutlined,
    QrcodeOutlined,
    RightOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { ChipGroup } from 'fragments/passcodes/fragments/visitor-pass-creation/chip-group';
import { EndPreset } from 'fragments/passcodes/fragments/visitor-pass-creation/pass-window';
import { presetKey } from 'fragments/passcodes/fragments/visitor-pass-creation/summary-label';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { VisitAccessFragmentProps } from './interfaces';
import { useVisitAccessController } from './visit-access.controller';
import { AccessPointPicker } from './access-point-picker';
import { VisitAccessSuccess } from './visit-access-success';
import 'fragments/passcodes/fragments/visit-access/visit-access.scss';
// ChipGroup renderiza las clases vp-chips/vp-chip, que viven en este archivo.
// Sin este import los chips salen sin estilo y ningún test lo detecta: jsdom no
// evalúa CSS. No es un import huérfano — borrarlo rompe la pantalla en silencio.
import 'fragments/passcodes/fragments/visitor-pass-creation/visitor-pass-creation.scss';

const PRESETS: Exclude<EndPreset, 'exact'>[] = ['p1', 'p2', 'p3', 'p7'];

export const VisitAccessFragment: React.FC<VisitAccessFragmentProps> = (props) => {
    const { translate } = useTranslator();
    const useController = props.useController ?? useVisitAccessController;
    const c = useController();
    const t = (key: string) => translate({ key });

    if (c.step === 1 && c.created) {
        return <VisitAccessSuccess controller={c} />;
    }

    // El selector ocupa la pantalla entera pero vive en la misma ruta: al no
    // desmontar el formulario, lo tipeado se conserva sin necesidad de un store.
    if (c.pickerOpen) {
        return (
            <AccessPointPicker
                points={c.points}
                selectedId={c.selectedPoint?.id}
                onSelect={c.onPointPicked}
                onClose={c.closePicker}
            />
        );
    }

    const nav = (
        <div className={'va-nav'}>
            <button className={'va-nav-back'} aria-label={t('visit-access.back')} onClick={c.onClose}>
                <ArrowLeftOutlined aria-hidden />
            </button>
        </div>
    );

    if (c.pointsLoadError) {
        return (
            <div className={'va-creation'}>
                {nav}
                <div className={'va-title'}>{t('visit-access.title')}</div>
                <div className={'va-empty'}>
                    <WifiOutlined aria-hidden />
                    <span>{t('visit-access.points-load-error')}</span>
                </div>
                <Button type={'primary'} className={'va-cta-btn'} onClick={c.retryLoadPoints}>
                    {t('visit-access.retry')}
                </Button>
            </div>
        );
    }

    if (!c.pointsLoaded) {
        return (
            <div className={'va-creation'}>
                {nav}
                <div className={'va-title'}>{t('visit-access.title')}</div>
            </div>
        );
    }

    if (c.points.length === 0) {
        return (
            <div className={'va-creation'}>
                {nav}
                <div className={'va-title'}>{t('visit-access.title')}</div>
                <div className={'va-empty'}>
                    <InfoCircleOutlined aria-hidden />
                    <span>{t('visit-access.no-points')}</span>
                </div>
            </div>
        );
    }

    const point = c.selectedPoint;
    const caps = c.capabilities;
    const pointLabel = point
        ? point.kind === 'reception'
            ? `${point.site_name} · ${t('visit-access.picker-reception')}`
            : `${point.site_name} · ${point.name}`
        : t('visit-access.point-placeholder');

    return (
        <div className={'va-creation'}>
            {nav}
            <div className={'va-title'}>{t('visit-access.title')}</div>

            <div className={'va-label'}>
                {t('visit-access.visitor')} <span className={'va-required'}>{t('visit-access.required')}</span>
            </div>
            <Input
                maxLength={120}
                size={'large'}
                style={{ width: '100%', borderRadius: 8, height: 48 }}
                placeholder={t('visit-access.visitor-placeholder')}
                value={c.visitorName}
                onChange={(e) => c.setVisitorName(e.target.value)}
            />

            <div className={'va-card'}>
                {/* Chevron propio (›) y aria-label propio: este control abre el
                    selector de punto, NO el panel de vigencia. Confundir los dos
                    fue exactamente el bug del gate anterior. */}
                <button
                    className={'va-card-row va-card-row-btn'}
                    aria-label={t('visit-access.change-point')}
                    onClick={c.openPicker}
                >
                    <EnvironmentOutlined aria-hidden />
                    <span className={`va-card-row-strong${point ? '' : ' va-card-row-empty'}`}>{pointLabel}</span>
                    <RightOutlined aria-hidden className={'va-card-row-chevron'} />
                </button>

                {point && caps && !c.panelOpen && (
                    <>
                        <div className={'va-card-row'}>
                            <ClockCircleOutlined aria-hidden />
                            <span>{c.validityLabel}</span>
                        </div>
                        <div className={'va-card-row'}>
                            {point.kind === 'reception' ? (
                                <QrcodeOutlined aria-hidden />
                            ) : (
                                <NumberOutlined aria-hidden />
                            )}
                            <span>{t(caps.deliveryKey)}</span>
                        </div>
                        {c.showTimezoneNote && (
                            <div className={'va-card-note'}>
                                <InfoCircleOutlined aria-hidden /> <span>{t('visit-access.timezone-note')}</span>
                            </div>
                        )}
                    </>
                )}

                {point && caps && c.panelOpen && (
                    <div className={'va-panel'} id={'va-panel'}>
                        {caps.canChooseStart && (
                            <div className={'va-group'}>
                                <div className={'va-group-label'}>{t('visit-access.start-label')}</div>
                                {c.startMode === 'now' ? (
                                    <>
                                        <div className={'va-group-value'}>{t('visit-access.start-now')}</div>
                                        <button className={'va-link'} onClick={() => c.setStartMode('exact')}>
                                            {t('visit-access.start-exact')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className={'va-pickers'}>
                                            <DatePicker
                                                style={{ width: '100%', borderRadius: 8, height: 48 }}
                                                format="DD/MM/YYYY"
                                                disabledDate={(cur) => !!cur && cur < moment().startOf('day')}
                                                onChange={(m) => c.setStartDate(m ? m.format('YYYY-MM-DD') : '')}
                                            />
                                            <TimePicker
                                                style={{ width: '100%', borderRadius: 8, height: 48 }}
                                                format="HH:mm"
                                                minuteStep={15}
                                                onChange={(m) => c.setStartTime(m ? m.format('HH:mm') : '')}
                                            />
                                        </div>
                                        <button className={'va-link'} onClick={() => c.setStartMode('now')}>
                                            {t('visit-access.start-back-now')}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        {/* La cerradura impone la ventana: en vez de un control
                            muerto va el motivo, y abajo la ventana ya resuelta. */}
                        {caps.fixedHours !== undefined && (
                            <div className={'va-fixed-note'}>
                                <InfoCircleOutlined aria-hidden />
                                <span>{t('visit-access.keypad-fixed-note')}</span>
                            </div>
                        )}

                        {caps.canChooseEnd && (
                            <div className={'va-group'}>
                                <div className={'va-group-label'}>{t('visit-access.expire-label')}</div>
                                {c.endPreset !== 'exact' ? (
                                    <>
                                        <ChipGroup
                                            label={t('visit-access.expire-label')}
                                            value={c.endPreset}
                                            options={PRESETS.map((p) => ({
                                                value: p,
                                                label: translate({ key: presetKey(p, c.startMode) }),
                                            }))}
                                            onChange={(v) => c.setEndPreset(v)}
                                        />
                                        <button className={'va-link'} onClick={() => c.setEndPreset('exact')}>
                                            {t('visit-access.expire-exact')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className={'va-pickers'}>
                                            <DatePicker
                                                style={{ width: '100%', borderRadius: 8, height: 48 }}
                                                format="DD/MM/YYYY"
                                                disabledDate={(cur) => !!cur && cur < moment().startOf('day')}
                                                onChange={(m) => c.setEndDate(m ? m.format('YYYY-MM-DD') : '')}
                                            />
                                            <TimePicker
                                                style={{ width: '100%', borderRadius: 8, height: 48 }}
                                                format="HH:mm"
                                                minuteStep={15}
                                                onChange={(m) => c.setEndTime(m ? m.format('HH:mm') : '')}
                                            />
                                        </div>
                                        <button className={'va-link'} onClick={() => c.setEndPreset('p1')}>
                                            {t('visit-access.expire-back-quick')}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        {caps.canChooseUses && (
                            <div className={'va-group'}>
                                <div className={'va-group-label'}>{t('visit-access.uses-title')}</div>
                                <ChipGroup
                                    label={t('visit-access.uses-title')}
                                    value={c.maxUses === undefined ? 'unlimited' : String(c.maxUses)}
                                    options={[
                                        { value: 'unlimited', label: t('visit-access.uses-unlimited') },
                                        { value: '1', label: '1' },
                                        { value: '5', label: '5' },
                                        { value: '10', label: '10' },
                                    ]}
                                    onChange={(v) => c.setMaxUses(v === 'unlimited' ? undefined : Number(v))}
                                />
                            </div>
                        )}

                        <div className={'va-resolved'}>
                            <ClockCircleOutlined aria-hidden /> <span>{c.validityLabel}</span>
                        </div>
                        {c.showTimezoneNote && (
                            <div className={'va-card-note'}>
                                <InfoCircleOutlined aria-hidden /> <span>{t('visit-access.timezone-note')}</span>
                            </div>
                        )}
                    </div>
                )}

                {point && (
                    <>
                        <div className={'va-card-divider'} />
                        <button
                            className={'va-card-toggle'}
                            aria-expanded={c.panelOpen}
                            aria-controls={'va-panel'}
                            onClick={c.togglePanel}
                        >
                            <span>{c.panelOpen ? t('visit-access.done') : t('visit-access.change')}</span>
                            {c.panelOpen ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
                        </button>
                    </>
                )}
            </div>

            {caps && <div className={'va-hint'}>{t(caps.hintKey)}</div>}

            <div className={'va-cta'}>
                {c.hasNetworkError && (
                    <div className={'va-neterror'}>
                        <WifiOutlined aria-hidden />
                        <span>{t('visit-access.net-error')}</span>
                    </div>
                )}
                {!c.hasNetworkError &&
                    c.validationError &&
                    c.validationError !== 'name' &&
                    c.validationError !== 'incomplete' && (
                        <div className={'va-warn'} role={'alert'}>
                            <InfoCircleOutlined aria-hidden />
                            <span>{t(`visit-access.err-${c.validationError}`)}</span>
                        </div>
                    )}
                <Button
                    type={'primary'}
                    className={'va-cta-btn'}
                    loading={c.isSubmitting}
                    disabled={!c.canSubmit}
                    onClick={c.submit}
                >
                    {c.isSubmitting ? t('visit-access.generating') : t('visit-access.generate')}
                </Button>
            </div>
        </div>
    );
};
